import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowDown, FaStar, FaShieldAlt, FaGift, FaChartLine, FaUsers, FaTrophy, FaMobile, FaCreditCard, FaGamepad, FaHeadset, FaLock, FaClock, FaCheckCircle, FaEnvelope, FaPlayCircle } from 'react-icons/fa';
import SEO from '../components/SEO';
import OfferCard from '../components/OfferCard';

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const offersRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const offers = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100',
      rating: 4.8,
      features: ['UK Licensed', 'Fast Payouts', 'Live Casino'],
      url: 'https://www.allbritishcasino.com/'
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      bonus: '200% up to £200',
      rating: 4.7,
      features: ['Exclusive Games', 'VIP Program', 'Mobile App'],
      url: 'https://www.partycasino.com/'
    },
    {
      id: 'plazaroyal',
      name: 'Plaza Royal',
      image: '/images/offers/plazaroyal.webp',
      bonus: '150% up to £150',
      rating: 4.6,
      features: ['24/7 Support', 'Crypto Accepted', 'Tournaments'],
      url: 'https://www.plazaroyal.com'
    },
  ];

  const features = [
    { icon: FaShieldAlt, title: 'Licensed & Secure', desc: 'All casinos are UKGC licensed and fully regulated' },
    { icon: FaGift, title: 'Exclusive Bonuses', desc: 'Access to special offers you won\'t find elsewhere' },
    { icon: FaChartLine, title: 'Expert Reviews', desc: 'In-depth analysis from our team of gaming experts' },
    { icon: FaUsers, title: 'Player Focused', desc: 'Reviews written by players, for players' },
  ];

  const stats = [
    { number: '500+', label: 'Casinos Reviewed' },
    { number: '50K+', label: 'Happy Players' },
    { number: '£2M+', label: 'Bonuses Claimed' },
    { number: '4.8/5', label: 'Average Rating' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ReelVindicator",
    "url": "https://reelvindicator.com",
    "description": "Expert UK casino reviews and exclusive bonus offers. Compare the best online casinos and find your perfect match.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://reelvindicator.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <SEO 
        title="UK Casino Reviews & Exclusive Offers"
        description="Discover the best UK online casinos with expert reviews, exclusive bonuses, and detailed comparisons. Find your perfect casino match today."
        schema={schema}
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-bg via-dark-surface to-dark-bg"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(255,107,53,0.1),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(247,147,30,0.1),transparent_50%)]"></div>
        </div>

        {/* Hero Images - Left and Right */}
        <div className="absolute inset-0 z-0">
          {/* Left Image - Desktop */}
          <div className="hidden lg:block absolute left-[5%] top-1/2 -translate-y-1/2 w-2/5 max-w-2xl opacity-40 hover:opacity-60 transition-opacity duration-500">
            <img
              src="/images/heromain1.webp"
              alt="Casino roulette wheel"
              className="w-full h-auto rounded-2xl shadow-2xl transform rotate-[-5deg] hover:rotate-0 transition-transform duration-500"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
          
          {/* Right Image - Desktop */}
          <div className="hidden lg:block absolute right-[5%] top-1/2 -translate-y-1/2 w-2/5 max-w-2xl opacity-40 hover:opacity-60 transition-opacity duration-500">
            <img
              src="/images/heromain2.webp"
              alt="Casino slot machine jackpot"
              className="w-full h-auto rounded-2xl shadow-2xl transform rotate-[5deg] hover:rotate-0 transition-transform duration-500"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>

          {/* Mobile Images - Stacked vertically */}
          <div className="lg:hidden absolute inset-0 flex flex-col items-center justify-center gap-4 px-4 pt-32 pb-20">
            <div className="w-full max-w-xs opacity-50">
              <img
                src="/images/heromain1.webp"
                alt="Casino roulette wheel"
                className="w-full h-auto rounded-xl shadow-xl"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="w-full max-w-xs opacity-50">
              <img
                src="/images/heromain2.webp"
                alt="Casino slot machine jackpot"
                className="w-full h-auto rounded-xl shadow-xl"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 animate-slide-up">
            <span className="text-gradient">Find Your Perfect</span>
            <br />
            <span className="text-white">Casino Match</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto animate-slide-up">
            Expert reviews, exclusive bonuses, and honest comparisons of the UK's top online casinos
          </p>

          {/* Hero Anchor Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-12 animate-slide-up">
            <button
              onClick={() => scrollTo(offersRef)}
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <FaGift /> Top Offers
            </button>
            <button
              onClick={() => scrollTo(featuresRef)}
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <FaStar /> Why Choose Us
            </button>
            <button
              onClick={() => scrollTo(reviewsRef)}
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <FaUsers /> Reviews
            </button>
            <button
              onClick={() => scrollTo(statsRef)}
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <FaChartLine /> Stats
            </button>
            <button
              onClick={() => scrollTo(ctaRef)}
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <FaTrophy /> Get Started
            </button>
            <Link
              to="/comparison"
              className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all transform hover:scale-105 flex items-center gap-2"
            >
              Compare All
            </Link>
          </div>

          <div className="animate-float">
            <FaArrowDown className="text-4xl text-accent-primary mx-auto opacity-50" />
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section ref={offersRef} className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="text-gradient">Exclusive Casino Offers</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Handpicked bonuses from trusted UK casinos. Claim your welcome bonus and start playing today.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/comparison"
              className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-4 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all transform hover:scale-105"
            >
              Compare All Offers
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="text-gradient">Why Trust ReelVindicator?</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            We're committed to providing honest, detailed reviews that help you make the best choices.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl hover:border-accent-primary transition-all transform hover:scale-105"
              >
                <feature.icon className="text-4xl text-accent-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-gradient mb-2">{stat.number}</div>
                <div className="text-gray-400 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Preview */}
      <section ref={reviewsRef} className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="text-gradient">Latest Expert Reviews</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {offers.map((offer) => (
              <div
                key={offer.id}
                className="glass p-6 rounded-xl hover:border-accent-primary transition-all transform hover:scale-105"
              >
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-48 object-contain rounded-lg mb-4"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="192"
                />
                <h3 className="text-2xl font-bold mb-2">{offer.name}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex text-accent-primary">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < Math.floor(offer.rating) ? 'fill-current' : 'text-gray-600'} />
                    ))}
                  </div>
                  <span className="text-gray-400">{offer.rating}/5</span>
                </div>
                <p className="text-gray-400 mb-4">Read our comprehensive review...</p>
                <div className="flex flex-col gap-2">
                  {offer.url && (
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-2 px-4 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all text-center"
                    >
                      Claim Bonus
                    </a>
                  )}
                  <Link
                    to={`/offers/${offer.id}`}
                    className="text-accent-primary font-semibold text-center hover:underline"
                  >
                    Read Review →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="text-gradient">Licensed & Regulated</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all">
              <FaShieldAlt className="text-5xl text-accent-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">UKGC Licensed</h3>
              <p className="text-gray-400 text-sm">All casinos are fully regulated</p>
            </div>
            <div className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all">
              <FaLock className="text-5xl text-accent-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">SSL Encrypted</h3>
              <p className="text-gray-400 text-sm">Bank-level security</p>
            </div>
            <div className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all">
              <FaCheckCircle className="text-5xl text-accent-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Fair Play</h3>
              <p className="text-gray-400 text-sm">RNG certified games</p>
            </div>
            <div className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all">
              <FaClock className="text-5xl text-accent-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Fast Payouts</h3>
              <p className="text-gray-400 text-sm">24-48 hour processing</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="text-gradient">How It Works</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Finding your perfect casino is simple with our step-by-step process
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Browse Reviews', desc: 'Explore our comprehensive casino reviews' },
              { step: '2', title: 'Compare Offers', desc: 'Compare bonuses and features side by side' },
              { step: '3', title: 'Choose Your Casino', desc: 'Select the perfect match for you' },
              { step: '4', title: 'Claim Bonus', desc: 'Sign up and claim your exclusive bonus' },
            ].map((item, index) => (
              <div key={index} className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-3 mt-4">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Categories Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="text-gradient">Popular Game Categories</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: FaGamepad, name: 'Slots', count: '5000+', color: 'from-blue-500 to-purple-500' },
              { icon: FaPlayCircle, name: 'Live Casino', count: '200+', color: 'from-green-500 to-teal-500' },
              { icon: FaTrophy, name: 'Table Games', count: '150+', color: 'from-yellow-500 to-orange-500' },
              { icon: FaGift, name: 'Jackpots', count: '50+', color: 'from-pink-500 to-red-500' },
            ].map((category, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl text-center hover:border-accent-primary transition-all transform hover:scale-105 cursor-pointer"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${category.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <category.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{category.name}</h3>
                <p className="text-gray-400">{category.count} Games</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="text-gradient">Secure Payment Methods</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            All our recommended casinos support a wide range of secure payment options
          </p>
          <div className="glass p-8 rounded-xl">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
              {['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bitcoin'].map((method, index) => (
                <div
                  key={index}
                  className="bg-dark-card p-4 rounded-lg text-center hover:bg-dark-hover transition-colors border border-dark-border hover:border-accent-primary"
                >
                  <FaCreditCard className="text-3xl text-accent-primary mx-auto mb-2" />
                  <span className="text-sm font-semibold">{method}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Gaming Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-gradient">Play Anywhere, Anytime</span>
              </h2>
              <p className="text-xl text-gray-400 mb-6">
                All recommended casinos offer fully optimized mobile experiences. Play your favorite games on the go with seamless performance across all devices.
              </p>
              <ul className="space-y-4 mb-8">
                {['iOS & Android Apps', 'Instant Play Browser', 'Responsive Design', 'Touch-Optimized Controls'].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <FaCheckCircle className="text-accent-primary flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/comparison"
                className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all"
              >
                Find Mobile Casinos
              </Link>
            </div>
            <div className="glass p-8 rounded-xl">
              <div className="flex items-center justify-center">
                <FaMobile className="text-9xl text-accent-primary opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="text-gradient">What Players Say</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah M.',
                rating: 5,
                text: 'Found the perfect casino through ReelVindicator. The reviews are spot-on and helped me avoid bad choices.',
                location: 'London, UK'
              },
              {
                name: 'James K.',
                rating: 5,
                text: 'The comparison tool is brilliant! Made it so easy to find a casino with the features I wanted.',
                location: 'Manchester, UK'
              },
              {
                name: 'Emma L.',
                rating: 5,
                text: 'Honest reviews and great bonuses. Claimed my welcome bonus and had a fantastic experience.',
                location: 'Birmingham, UK'
              },
            ].map((testimonial, index) => (
              <div key={index} className="glass p-6 rounded-xl hover:border-accent-primary transition-all">
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-accent-primary fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.location}</p>
                  </div>
                  <FaUsers className="text-3xl text-accent-primary opacity-30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Support Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="glass p-12 rounded-xl text-center max-w-4xl mx-auto">
            <FaHeadset className="text-6xl text-accent-primary mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient">24/7 Customer Support</span>
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              All recommended casinos offer round-the-clock support via live chat, email, and phone. Get help whenever you need it.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: FaHeadset, title: 'Live Chat', desc: 'Instant responses' },
                { icon: FaEnvelope, title: 'Email Support', desc: 'Detailed assistance' },
                { icon: FaMobile, title: 'Phone Support', desc: 'Direct contact' },
              ].map((support, index) => (
                <div key={index} className="bg-dark-card p-6 rounded-lg">
                  <support.icon className="text-3xl text-accent-primary mx-auto mb-3" />
                  <h3 className="font-bold mb-2">{support.title}</h3>
                  <p className="text-sm text-gray-400">{support.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="glass p-12 rounded-xl max-w-3xl mx-auto text-center">
            <FaEnvelope className="text-5xl text-accent-primary mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient">Stay Updated</span>
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Get the latest casino offers, reviews, and exclusive bonuses delivered to your inbox
            </p>
            <div className="flex flex-col md:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-dark-card border border-dark-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-accent-primary"
              />
              <button className="bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-3 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible Gaming Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              <span className="text-gradient">Responsible Gaming</span>
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              We promote safe and responsible gambling. All recommended casinos provide tools to help you stay in control, including deposit limits, self-exclusion, and reality checks.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="https://www.gambleaware.org" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                <img src="/images/footer/gambleaware.webp" alt="GambleAware" className="h-12" loading="lazy" decoding="async" width="180" height="48" />
              </a>
              <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                <img src="/images/footer/gamcare.webp" alt="GamCare" className="h-12" loading="lazy" decoding="async" width="180" height="48" />
              </a>
              <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                <img src="/images/footer/therapy.webp" alt="BeGambleAware" className="h-12" loading="lazy" decoding="async" width="180" height="48" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 bg-gradient-to-br from-dark-surface to-dark-bg">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-gradient">Ready to Start Winning?</span>
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Join thousands of players who trust ReelVindicator for the best casino offers and reviews.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/comparison"
              className="bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-4 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all transform hover:scale-105"
            >
              Compare Offers Now
            </Link>
            <Link
              to="/about"
              className="glass px-8 py-4 rounded-lg hover:bg-accent-primary hover:text-white transition-all font-semibold"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;

